import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { COMPETENCY_PERMS as P, useCompetencyCan, useTrainingCourses, useTrainingSessions } from '@/hooks/useCompetency';
import type { TrainingSession } from '@/services/competency.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR } from './competencyDisplay';

const TrainingSessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const canManage = useCompetencyCan(P.trainingManage);
  const [status, setStatus] = useState<string>();
  const [courseId, setCourseId] = useState<string>();
  const [search, setSearch] = useState('');
  const { data = [], isLoading } = useTrainingSessions({ status, course_id: courseId, search });
  const { data: courses = [] } = useTrainingCourses();

  const columns: ColumnsType<TrainingSession> = [
    {
      title: 'Session',
      dataIndex: 'session_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/sessions/${r.id}`)}>{v}</a>,
    },
    { title: 'Date', dataIndex: 'session_date', width: 110, render: (v) => formatDate(v) },
    { title: 'Training', render: (_, r) => `${r.course_code ?? ''} ${r.course_title ?? ''}`.trim() },
    { title: 'Topic', dataIndex: 'topic', ellipsis: true },
    { title: 'Trainer', dataIndex: 'trainer_display', width: 170 },
    { title: 'Attendees', width: 110, align: 'right', render: (_, r) => `${r.present_count ?? 0} / ${r.attendee_count ?? 0}` },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: 'Effectiveness',
      width: 140,
      render: (_, r) =>
        r.evaluation_id ? (
          <a onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness/${r.evaluation_id}`)}>
            <Tag color={STATUS_COLOR[r.evaluation_status ?? '']}>{r.evaluation_status}</Tag>
          </a>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Training attendance"
        extra={
          canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${COMPETENCY_BASE}/sessions/new`)}>
              New session
            </Button>
          ) : null
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search session, topic or trainer" allowClear style={{ width: 280 }} onSearch={setSearch} />
          <Select allowClear placeholder="Status" style={{ width: 150 }} value={status} onChange={setStatus} options={['Planned', 'Completed', 'Cancelled'].map((s) => ({ value: s, label: s }))} />
          <Select
            allowClear
            placeholder="Course"
            style={{ width: 260 }}
            value={courseId}
            onChange={setCourseId}
            showSearch
            optionFilterProp="label"
            options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
          />
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>
    </div>
  );
};

export default TrainingSessionsPage;
